import type { WatchPart } from '../api/client'

export type PartVariantOption = {
  variant_id: string
  title: string
  price: number
  available: boolean
  option_values?: Record<string, string>
  source_url?: string
  image_url?: string | null
}

export type PartOptionDefinition = {
  name: string
  position: number
  values: string[]
}

export function isConsolidatedPart(part: WatchPart): boolean {
  return Boolean(part.metadata?.consolidated && Array.isArray(part.metadata?.variants) && part.metadata.variants.length > 0)
}

export function partVariants(part: WatchPart): PartVariantOption[] {
  const raw = part.metadata?.variants
  if (!Array.isArray(raw)) return []
  return raw
    .filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'))
    .map((item) => ({
      variant_id: String(item.variant_id ?? ''),
      title: String(item.title ?? ''),
      price: Number(item.price ?? 0),
      available: Boolean(item.available ?? true),
      option_values:
        item.option_values && typeof item.option_values === 'object'
          ? Object.fromEntries(
              Object.entries(item.option_values as Record<string, unknown>).map(([key, value]) => [
                key,
                String(value),
              ]),
            )
          : undefined,
      source_url: typeof item.source_url === 'string' ? item.source_url : undefined,
      image_url: typeof item.image_url === 'string' ? item.image_url : null,
    }))
    .filter((variant) => variant.variant_id)
}

export function selectedVariantId(part: WatchPart): string | null {
  const selected = part.metadata?.selected_variant_id ?? part.metadata?.default_variant_id ?? part.metadata?.variant_id
  return selected != null ? String(selected) : null
}

export function findPartVariant(part: WatchPart, variantId: string): PartVariantOption | null {
  return partVariants(part).find((variant) => variant.variant_id === variantId) ?? null
}

export function applyCaseVariantSelection(part: WatchPart, variantId: string): WatchPart | null {
  const variant = findPartVariant(part, variantId)
  if (!variant) return null

  return {
    ...part,
    price: variant.price,
    image_url: variant.image_url ?? part.image_url,
    purchase_url: variant.source_url ?? part.purchase_url,
    metadata: {
      ...part.metadata,
      selected_variant_id: variant.variant_id,
      variant_id: variant.variant_id,
      source_url: variant.source_url ?? part.metadata?.source_url,
      selected_price: variant.price,
      ...(variant.image_url ? { selected_image_url: variant.image_url } : {}),
    },
  }
}

export function optionDefinitions(part: WatchPart): PartOptionDefinition[] {
  const raw = part.metadata?.option_definitions
  if (!Array.isArray(raw)) return []
  return raw
    .filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'))
    .map((item) => ({
      name: String(item.name ?? ''),
      position: Number(item.position ?? 0),
      values: Array.isArray(item.values) ? item.values.map(String) : [],
    }))
}
